import React, { useState, useEffect } from "react";
import "./AboutUs.css";
import cancel from "../../assets/cancel.png";
import { storage } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Loader from "../../Components/Loader/Loader";
import api from "../../utils/api";
import instagramIcon from "../../assets/instagram.png";
import facebookIcon from "../../assets/facebook.png";
import twitterIcon from "../../assets/twitter.png";
import linkedinIcon from "../../assets/linkedin.png";
import youtubeIcon from "../../assets/youtube.png";
import { z } from "zod";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { toast } from "react-hot-toast";
import { useSearch } from "../../context/SearchContext";

const aboutUsSchema = z.object({
  description: z
    .string()
    .min(10, "Description must be at least 10 characters."),
  phone: z
    .string(),
  email: z.string().email("Invalid email address"),
  address: z.string().min(5, "Address is too short"),
  facebook: z.string().url("Invalid Facebook URL").optional().or(z.literal("")),
  twitter: z.string().url("Invalid Twitter URL").optional().or(z.literal("")),
  instagram: z
    .string()
    .url("Invalid Instagram URL")
    .optional()
    .or(z.literal("")),
  youtube: z.string().url("Invalid YouTube URL").optional().or(z.literal("")),
  linkedin: z.string().url("Invalid LinkedIn URL").optional().or(z.literal("")),
});

const AboutUs = () => {
  const { search } = useSearch();
  const token = localStorage.getItem("accessToken");
  const [errors, setErrors] = useState({});
  const [loadingData, setLoadingData] = useState(true);
  const [value, setValue] = useState(0);
  const [data, setData] = useState({});
  const [details, setDetails] = useState({
    description: "",
    phone: "",
    email: "",
    address: "",
    facebook: "",
    twitter: "",
    instagram: "",
    youtube: "",
    linkedin: "",
  });
  const [image, setImage] = useState(null);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [mapPreview, setMapPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleClick1 = () => setValue(1);
  const handleClick2 = () => setValue(0);

  const fetchAboutData = async () => {
    setLoadingData(true);
    try {
      const response = await api.get("/about?search=" + search);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to fetch About Us data");
      setData({});
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchAboutData();
  }, [search]);

  useEffect(() => {
    if (data) {
      setDetails({
        description: data.description || "",
        phone: data.phone || "",
        email: data.email || "",
        address: data.address || "",
        facebook: data.social_media?.facebook || "",
        twitter: data.social_media?.twitter || "",
        instagram: data.social_media?.instagram || "",
        youtube: data.social_media?.youtube || "",
        linkedin: data.social_media?.linkedin || "",
      });
      
      if (data.logo) {
        setLogoPreview(data.logo);
      }
      if (data.picture) {
        setMapPreview(data.picture);
      }
    }
  }, [data]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const result = aboutUsSchema.safeParse(details);
    if (!result.success) {
      setErrors(result.error.flatten().fieldErrors);
      toast.error("Please fix the errors in the form.");
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      let downloadUrl = data.picture || "";
      let logoUrl = data.logo || "";

      if (image) {
        const storageRef = ref(storage, `aboutUs/map`);
        await uploadBytes(storageRef, image);
        downloadUrl = await getDownloadURL(storageRef);
      }
      if (logo) {
        const logoRef = ref(storage, "aboutUs/logo");
        await uploadBytes(logoRef, logo);
        logoUrl = await getDownloadURL(logoRef);
      }

      await api.post(`/about`, {
        description: details.description,
        phone: details.phone,
        address: details.address,
        email: details.email,
        picture: downloadUrl,
        logo: logoUrl,
        socialMedia: {
          facebook: details.facebook,
          twitter: details.twitter,
          instagram: details.instagram, 
          youtube: details.youtube,
          linkedin: details.linkedin,
        },
      });

      await fetchAboutData();
      
      if (image) {
        if (document.getElementById("mapInput"))
          document.getElementById("mapInput").value = "";
      }
      if (logo) {
        if (document.getElementById("logoInput"))
          document.getElementById("logoInput").value = "";
      }

      setValue(0);
      toast.success("About Us updated successfully!");
    } catch (err) {
      console.error("Upload failed", err);
      toast.error("Failed to update About Us.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));

    const tempDetails = {
      ...details,
      [name]: value,
    };

    const result = aboutUsSchema.safeParse(tempDetails);
    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: fieldErrors[name] || [],
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: [],
      }));
    }
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (file && isImage(file.name)) {
      setImage(file);
      setMapPreview(URL.createObjectURL(file));
    } else {
      e.target.value = "";
      toast.error("Please select a valid image file for the map.");
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file && isImage(file.name)) {
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    } else {
      e.target.value = "";
      toast.error("Please select a valid image file for the logo.");
    }
  };

  function getExtension(filename) {
    var parts = filename.split(".");
    return parts[parts.length - 1];
  }

  function isImage(filename) {
    var ext = getExtension(filename);
    switch (ext.toLowerCase()) {
      case "jpeg":
      case "jpg":
      case "png":
        return true;
    }
    return false;
  }

  return (
    <div className="about_us">
      {value === 1 && loadingData ? (
        <Loader />
      ) : (
        <form onSubmit={onSubmit}>
          <div
            className={
              value === 0
                ? "about_us__form_div_not_open"
                : "about_us__form_div_open"
            }
          >
            <div className="about_us__form">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>  
              <h1 style={{fontSize: '24px'}}>Add About Us</h1>
              <div className="about_us__from_img" onClick={handleClick2}>
                <img src={cancel} alt="cancel" />
              </div>
              </div>
              <div className="about_us__form_grid">
                <div className="about_us__form_input">
                  <div style={{ display: "flex" }}>
                    <h5 style={{ marginTop: "3px" }}>School Logo:</h5> &nbsp;
                    <input
                      type="file"
                      id="logoInput"
                      accept="image/png, image/jpeg"
                      onChange={handleLogoUpload}
                    />
                  </div>
                  {logoPreview && (
                    <img
                      src={logoPreview}
                      alt="Logo Preview"
                      className="about_us__form_preview_image"
                    />
                  )}
                </div>
                <div className="about_us__form_input">
                  <div style={{ display: "flex" }}>
                    <h5 style={{ marginTop: "3px" }}>Map Screenshot:</h5> &nbsp;
                    <input
                      type="file"
                      id="mapInput"
                      accept="image/png, image/jpeg"
                      onChange={handleUpload}
                    />
                  </div>
                  {mapPreview && (
                    <img
                      src={mapPreview}
                      alt="Map Preview"
                      className="about_us__form_preview_image"
                    />
                  )}
                </div>
                <div className="about_us__form_input">
                  <h5>About Us</h5>
                  <textarea
                    name="description"
                    id="descriptionInput"
                    onChange={handleChange}
                    className="about_us__form_text_input"
                    value={details.description}
                    placeholder="Enter school description"
                  />
                  {errors.description && (
                    <p className="error-message">{errors.description[0]}</p>
                  )}
                </div>
                <div className="about_us__form_input">
                  <h5>Phone</h5>
                  <input
                    type="number"
                    name="phone"
                    id="phoneInput"
                    onChange={handleChange}
                    pattern="[6789]\d{9}"
                    maxLength="10"
                    className="about_us__form_input_small"
                    value={details.phone}
                    placeholder="Enter phone number"
                  />
                  {errors.phone && (
                    <p className="error-message">{errors.phone[0]}</p>
                  )}
                </div>
                <div className="about_us__form_input">
                  <h5>Email</h5>
                  <input
                    type="email"
                    name="email"
                    id="emailInput"
                    onChange={handleChange}
                    className="about_us__form_input_small"
                    value={details.email}
                    placeholder="Enter email address"
                  />
                  {errors.email && (
                    <p className="error-message">{errors.email[0]}</p>
                  )}
                </div>
                <div className="about_us__form_input">
                  <h5>Address</h5>
                  <input
                    type="text"
                    name="address"
                    id="addressInput"
                    onChange={handleChange}
                    className="about_us__form_input_small"
                    value={details.address}
                    placeholder="Enter school address"
                  />
                  {errors.address && (
                    <p className="error-message">{errors.address[0]}</p>
                  )}
                </div>
                <div className="about_us__form_input">
                  <h5>Facebook</h5>
                  <input
                    type="url"
                    name="facebook"
                    id="facebookInput"
                    placeholder="Enter Facebook URL"
                    onChange={handleChange}
                    className="about_us__form_input_small"
                    value={details.facebook}
                  />
                  {errors.facebook && (
                    <p className="error-message">{errors.facebook[0]}</p>
                  )}
                </div>
                <div className="about_us__form_input">
                  <h5>Twitter</h5>
                  <input
                    type="url"
                    name="twitter"
                    id="twitterInput"
                    placeholder="Enter Twitter URL"
                    onChange={handleChange}
                    className="about_us__form_input_small"
                    value={details.twitter}
                  />
                  {errors.twitter && (
                    <p className="error-message">{errors.twitter[0]}</p>
                  )}
                </div>
                <div className="about_us__form_input">
                  <h5>Instagram</h5>
                  <input
                    type="url"
                    name="instagram"
                    id="instagramInput"
                    placeholder="Enter Instagram URL"
                    onChange={handleChange}
                    className="about_us__form_input_small"
                    value={details.instagram}
                  />
                  {errors.instagram && (
                    <p className="error-message">{errors.instagram[0]}</p>
                  )}
                </div>
                <div className="about_us__form_input">
                  <h5>YouTube</h5>
                  <input
                    type="url"
                    name="youtube"
                    id="youtubeInput"
                    placeholder="Enter YouTube URL"
                    onChange={handleChange}
                    className="about_us__form_input_small"
                    value={details.youtube}
                  />
                  {errors.youtube && (
                    <p className="error-message">{errors.youtube[0]}</p>
                  )}
                </div>
                <div className="about_us__form_input">
                  <h5>LinkedIn</h5>
                  <input
                    type="url"
                    name="linkedin"
                    id="linkedinInput"
                    placeholder="Enter LinkedIn URL"
                    onChange={handleChange}
                    className="about_us__form_input_small"
                    value={details.linkedin}
                  />
                  {errors.linkedin && (
                    <p className="error-message">{errors.linkedin[0]}</p>
                  )}
                </div>
              </div>
              <div>
                <button className="about_us__form_button" type="submit">
                  {loading ? (
                    <Loader small={true} />
                  ) : (
                    <span style={{ color: "#fff" }}>Submit</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}

      <div className="about_us__top">
        <div className="about_us__top_div">School Logo</div>
        <button className="about_us__top_button" onClick={handleClick1}>
          Add About Us
        </button>
      </div>
      <div className="about_us__middle">
        <div>
          <LazyLoadImage
            src={data?.logo}
            alt="logo"
            className="about_us__middle_logo"
          />
        </div>
        <div className="about_us__middle_div">About School</div>
        <div className="about_us__middle_p_map">
          <p>{data ? data.description : "loading..."}</p>
          <LazyLoadImage
            src={data?.picture}
            alt="map"
            className="about_us__middle_logo"
          />
        </div>
      </div>
      <div className="about_us__bottom">
        <div className="about_us__bottom_contact">Contact Info</div>
        <div className="about_us__bottom_contact_div">
          <p>Phone: {data ? data.phone : "loading..."}</p>
          <p>E-mail: {data ? data.email : "loading..."}</p>
          <p>Address: {data ? data.address : "loading..."}</p>
        </div>
      </div>
      <div className="about_us__bottom about_us__social_media">
        <div className="about_us__bottom_contact">Follow Us</div>
        <div className="about_us__bottom_contact">
          <div className="about_us__social_media_links">
            {data?.social_media?.facebook && (
              <a
                href={data.social_media.facebook}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={facebookIcon} alt="Facebook" />
              </a>
            )}
            {data?.social_media?.twitter && (
              <a
                href={data.social_media.twitter}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={twitterIcon} alt="Twitter" />
              </a>
            )}
            {data?.social_media?.instagram && (
              <a
                href={data.social_media.instagram}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={instagramIcon} alt="Instagram" />
              </a>
            )}
            {data?.social_media?.youtube && (
              <a
                href={data.social_media.youtube}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={youtubeIcon} alt="YouTube" />
              </a>
            )}
            {data?.social_media?.linkedin && (
              <a
                href={data.social_media.linkedin}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={linkedinIcon} alt="LinkedIn" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;